import type { MpRequestContext, MpRequestCreate, MpRequestChangeRef, MpRequestType } from '@/lib/apiClient';

/**
 * The Manpower Request form's rules (#303 slice 2; the SYSTAC door, slice 4),
 * kept out of the component so they are testable without a DOM — the same split
 * the crew grid makes with `manpower/lib/scheduleModel.ts`.
 *
 * Everything here decides WHAT GETS SENT, WHAT THE FORM SAYS, or WHO IS TOLD
 * NO. Nothing here renders.
 *
 * ONE form, two faces. On a won or real job it is the PM's crew request. On a
 * SYSTAC bid it is the ESTIMATOR's forecast update (#386): same fields, same
 * table, same inbox — but the job decides which it is (`ctx.requestType`), the
 * copy changes to match, and the fields open pre-filled from the forecast the
 * Tracker already holds so the estimator states what CHANGED.
 */

export type RequestFormValues = {
  stage: 'initial' | 'change';
  /** The change order this crew is for, as the <select>'s string value; '' = the job itself. */
  changeId: string;
  crewSize: string;
  phase: '' | 'fabrication' | 'installation';
  startDate: string;
  endDate: string;
  reason: string;
  staffingNotes: string;
};

export const EMPTY_REQUEST_FORM: RequestFormValues = {
  stage: 'initial', changeId: '', crewSize: '', phase: '', startDate: '', endDate: '', reason: '', staffingNotes: '',
};

/** The slice of context the form's rules read. Everything optional: the form
 *  mounts before the context arrives. */
export type RequestFormContext = Partial<Pick<MpRequestContext, 'suggestedStage' | 'requestType' | 'forecast' | 'changes'>>;

/** Is this the estimator's face of the form? False until the context says so. */
export function isForecastRequest(ctx: RequestFormContext | undefined): boolean {
  return ctx?.requestType === 'systac-forecast';
}

/** The change the form currently points at, or null for "the job itself" / not found. */
export function selectedChange(ctx: RequestFormContext | undefined, changeId: string): MpRequestChangeRef | null {
  if (!changeId) return null;
  return ctx?.changes?.find((c) => String(c.id) === changeId) ?? null;
}

/**
 * The type this submission takes — the client mirror of the API's
 * `requestTypeFor`. A bid is always a forecast; crew for a change is `pco`
 * until that change is linked to an Intacct CO, then `co`; otherwise the PM's
 * ordinary request. Never chosen by the user — derived from what they pointed
 * the form at. The API refuses any other pairing.
 */
export function requestTypeFor(ctx: RequestFormContext | undefined, changeId: string): MpRequestType {
  if (isForecastRequest(ctx)) return 'systac-forecast';
  const change = selectedChange(ctx, changeId);
  if (change) return change.executed ? 'co' : 'pco';
  return ctx?.requestType ?? 'pm-change';
}

/** "PCO-003 — Add east slab" for the select and the hints. */
export function changeLabel(c: Pick<MpRequestChangeRef, 'changeNo' | 'description' | 'executed'>): string {
  const desc = c.description ? ` — ${c.description}` : '';
  return `${c.changeNo}${desc}${c.executed ? ' (executed CO)' : ''}`;
}

/** The stage the form starts on. Crew already booked reads as a change; on a
 *  bid, a forecast update already sent from here reads as a change. */
export function defaultStage(ctx: RequestFormContext | undefined): 'initial' | 'change' {
  return ctx?.suggestedStage ?? 'initial';
}

/**
 * The stage default for what the form is POINTED AT. A change carries its own
 * signal — has crew been requested for THIS change before? — because the
 * job-wide one (published base crew) says nothing about it: a brand-new PCO on
 * a job with crew is a first ask, not "a change to what was already proposed"
 * (review of #442). Applied when the selection changes, unless the user has
 * already chosen a stage by hand.
 */
export function suggestedStageFor(ctx: RequestFormContext | undefined, changeId: string): 'initial' | 'change' {
  const change = selectedChange(ctx, changeId);
  if (change) return change.suggestedStage ?? (change.hasRequest ? 'change' : 'initial');
  return defaultStage(ctx);
}

/**
 * 'YYYY-MM-DD' plus N calendar days, or null when either half is missing.
 * Pure string arithmetic through UTC so the viewer's zone cannot shift the day.
 */
export function addDays(ymd: string | null | undefined, days: number | null | undefined): string | null {
  if (!ymd || days == null || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return null;
  d.setUTCDate(d.getUTCDate() + Math.trunc(days));
  return d.toISOString().slice(0, 10);
}

/**
 * Fill the BLANK fields from the bid forecast, on a forecast request only.
 *
 * Only blank ones: the context can arrive after the estimator has started
 * typing (first visit to a job) and a refetch after staleTime can bring it
 * again — neither may overwrite a value they entered. A crew request never
 * seeds anything: a won job's crew is the schedule's business, not a forecast.
 * Peak crew goes in as text — the field is free text and stays so.
 *
 * ⚠️ The finish is INCLUSIVE, exactly as the crew grid draws the forecast
 * (`scheduleModel.systacForecast`: `addYmd(start, days - 1)`). A 45-day bid
 * from Nov 2 runs THROUGH Dec 16. Adding the full duration put the form one day
 * past the grid (review of #441) — two dates for one forecast.
 */
export function seedFromContext(v: RequestFormValues, ctx: RequestFormContext | undefined): RequestFormValues {
  if (!isForecastRequest(ctx) || !ctx?.forecast) return v;
  const f = ctx.forecast;
  const start = v.startDate || f.startDate || '';
  return {
    ...v,
    crewSize:  v.crewSize  || (f.peakCrew != null ? String(f.peakCrew) : ''),
    startDate: start,
    endDate:   v.endDate   || forecastFinish(start || null, f.durationDays) || '',
  };
}

/** The last day of an N-day span starting on `start`, inclusive — the grid's rule. */
export function forecastFinish(start: string | null, durationDays: number | null | undefined): string | null {
  if (durationDays == null || durationDays < 1) return null;
  return addDays(start, durationDays - 1);
}

/**
 * The values the form holds the moment it opens.
 *
 * ⚠️ Stage comes from the context that is ALREADY CACHED, not from "initial"
 * (review of #439). The card and the modal share one query key, so by the time
 * the PM opens the form the context is usually in the cache and the modal's
 * ctx-effect has already run — once. An open-reset to a bare "initial" then
 * overwrote it, and the effect never fired again because the context object
 * had not changed. Result: every job with crew already booked opened on
 * "First crew for this job". The reset and the first render both go through
 * here so that cannot recur. The forecast pre-fill rides the same path.
 */
export function initialFormValues(ctx: RequestFormContext | undefined): RequestFormValues {
  return seedFromContext({ ...EMPTY_REQUEST_FORM, stage: defaultStage(ctx) }, ctx);
}

/**
 * Client-side check mirroring what the API will refuse, so the PM hears it
 * before the round trip. Returns the message to show, or null when the form may
 * be sent.
 */
export function validateRequestForm(v: RequestFormValues, ctx?: RequestFormContext): string | null {
  const forecast = isForecastRequest(ctx);
  if (!v.crewSize.trim()) {
    return forecast
      ? 'Say the peak crew you now expect — a range like "10-12" is fine.'
      : 'Say how many people you need — a range like "2-3" is fine.';
  }
  if (!v.reason.trim()) {
    return forecast
      ? 'Say what changed. This is what the schedulers read.'
      : 'Say why. This is what the scheduler reads.';
  }
  if (v.startDate && v.endDate && v.endDate < v.startDate) return 'The end date is before the start date.';
  return null;
}

/**
 * Form values → the POST body. Crew size and reason go trimmed but otherwise AS
 * TYPED — "2-3" is an answer, not a parse error. Phase is sent only where it
 * means something: a fab job's two crews. The type is the job's, never chosen.
 */
export function toPayload(projectId: number, ctx: MpRequestContext, v: RequestFormValues): MpRequestCreate {
  const change = selectedChange(ctx, v.changeId);
  return {
    projectId,
    requestType: requestTypeFor(ctx, v.changeId),
    changeOrderId: change?.id ?? null,
    stage: v.stage,
    crewSize: v.crewSize.trim(),
    phase: ctx.isFab && v.phase ? v.phase : null,
    startDate: v.startDate || null,
    endDate: v.endDate || null,
    reason: v.reason.trim(),
    staffingNotes: v.staffingNotes.trim() || null,
  };
}

/**
 * Why the form is closed to this caller on this job, in the caller's words —
 * or null when it is open.
 *
 * A SYSTAC bid is no longer closed to everyone (slice 4): it is the estimator's
 * door. So the only question on any job is whether THIS caller is the owner —
 * the estimator on a bid, the primary PM otherwise — and the message names the
 * owner it is looking for.
 */
export function blockedMessage(ctx: MpRequestContext | undefined): string | null {
  if (!ctx || ctx.canRequest) return null;
  if (ctx.lifecycle === 'systac') {
    return `Only this bid's estimator${ctx.estimator?.name ? ` (${ctx.estimator.name})` : ''} can update its SYSTAC forecast. Covering for them? Ask an admin for the manpower request permission.`;
  }
  return `Only this job's primary PM${ctx.pm?.name ? ` (${ctx.pm.name})` : ''} can request crew on it. Covering for them? Ask an admin for the manpower request permission.`;
}

/** Every string the two faces of the form disagree on, in one place. */
export type FormCopy = {
  title: string;
  button: string;
  stageLegend: string;
  stageInitial: string;
  stageChange: string;
  stageHint: string;
  crewLabel: string;
  crewHint: string;
  startLabel: string;
  endLabel: string;
  reasonLabel: string;
  reasonPlaceholder: string;
  staffingLabel: string;
  sentTitle: (id: number) => string;
  sentBody: string;
};

const CREW_REQUEST_COPY: FormCopy = {
  title: 'Request manpower',
  button: 'Send request',
  stageLegend: 'What is this?',
  stageInitial: 'First crew for this job',
  stageChange: 'A change to crew already booked',
  stageHint: 'This job already has crew on the schedule — is this a change?',
  crewLabel: 'How many people',
  crewHint: 'Say it how you would say it to the scheduler. A range is fine.',
  startLabel: 'From',
  endLabel: 'Through',
  reasonLabel: 'Why',
  reasonPlaceholder: 'What changed, or what the job needs. If crew is moving from another job, say which.',
  staffingLabel: 'Anything specific about who',
  sentTitle: (id) => `Request #${id} sent to the schedulers.`,
  sentBody: 'A scheduler will review it with you and book the crew on the schedule by hand — nothing on the grid changes until they do.',
};

const FORECAST_COPY: FormCopy = {
  title: 'Update the SYSTAC forecast',
  button: 'Send update',
  stageLegend: 'What is this?',
  stageInitial: 'First forecast sent for this bid',
  stageChange: 'An update to the forecast',
  stageHint: 'A forecast update has already gone out for this bid — is this another?',
  crewLabel: 'Peak crew you now expect',
  crewHint: 'Pre-filled from the bid. Change it if the forecast has moved; a range is fine.',
  startLabel: 'Anticipated start',
  endLabel: 'Anticipated finish',
  reasonLabel: 'What changed',
  reasonPlaceholder: 'What moved, and why — scope, schedule, a competitor, the owner. This is what the schedulers read.',
  staffingLabel: 'Anything else the schedulers should know',
  sentTitle: (id) => `Forecast update #${id} sent.`,
  sentBody: 'The schedulers see it in the request inbox, and it is logged on the job — the bid’s forecast in Bid Tracker is unchanged.',
};

/**
 * Crew for a change order: `pco` while the change is proposed, `co` once it is
 * an executed CO. Product owner, 2026-09-02: PCO manpower is "essentially proposed
 * manpower", treated like a SYSTAC forecast — the schedulers can put it on the
 * schedule as ANTICIPATED crew so everyone sees what may be coming, and the
 * request becomes a CO request by itself when the change is linked in Intacct.
 */
function changeCopy(change: Pick<MpRequestChangeRef, 'changeNo' | 'executed'>): FormCopy {
  const no = change.changeNo;
  if (change.executed) {
    return {
      ...CREW_REQUEST_COPY,
      title: `Request manpower for ${no}`,
      stageInitial: `First crew for ${no}`,
      stageChange: `A change to crew already booked for ${no}`,
      reasonPlaceholder: `What ${no} needs, and when. If crew is moving from another job, say which.`,
      sentTitle: (id) => `Request #${id} for ${no} sent to the schedulers.`,
    };
  }
  return {
    ...CREW_REQUEST_COPY,
    title: `Proposed manpower for ${no}`,
    button: 'Send proposed crew',
    stageInitial: `First crew proposed for ${no}`,
    stageChange: `A change to what was already proposed for ${no}`,
    stageHint: `Crew has already been proposed for ${no} — is this a change to it?`,
    crewLabel: 'Crew you expect this change to need',
    crewHint: 'Proposed, not booked — the schedulers can show it on the schedule as anticipated crew. A range is fine.',
    startLabel: 'Expected from',
    endLabel: 'Expected through',
    reasonLabel: 'What the change involves',
    reasonPlaceholder: `What ${no} adds, and what it will take. The schedulers read this to anticipate the crew.`,
    sentTitle: (id) => `Proposed crew #${id} for ${no} sent to the schedulers.`,
    sentBody: `This is proposed manpower — the schedulers can put it on the schedule as anticipated crew. It becomes a CO request by itself the moment ${no} is linked to an Intacct CO; if ${no} is rejected, it is declined by itself.`,
  };
}

export function formCopy(ctx: RequestFormContext | undefined, changeId = ''): FormCopy {
  if (isForecastRequest(ctx)) return FORECAST_COPY;
  const change = selectedChange(ctx, changeId);
  return change ? changeCopy(change) : CREW_REQUEST_COPY;
}
